/* eslint-disable react/display-name */
import React, { FC, forwardRef, memo } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Switch,
  TextField
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {  DatePicker } from '@mui/x-date-pickers';
import i18next from 'i18next';

const useStyles = makeStyles(theme => ({
  shrink: {
    transform: 'translate(14px, -4.5px) scale(0.75)'
  },
  animated: {

  }
}));

export const FormField: FC<any>  = ({
  onChange, value, name, hint, style, type, floatingLabelText, ...props
}) => (
  <TextField
    name={name}
    hintText={hint}
    type={type || 'text'}
    value={value}
    onChange={onChange}
    floatingLabelText={floatingLabelText || ''}
    style={style || {}}
    {...props}
  />
);

export const ToggleField: FC<any> = ({ value, checked, onToggle, label='', variant, disabled = false, size, labelStyle, ...props }) => {
  return (
    <FormControlLabel
      label={label}
      style={labelStyle || {}}
      control={
        <Switch
          checked={checked}
          color={variant || 'primary'}
          size={size || 'medium'}
          onChange={onToggle}
          disabled={disabled || false}
          value={value} {...props}/>
      }
    />);
};

export const NewTextField: FC<any> = memo(forwardRef(({
  id, 
  label,
  type,
  value,
  className,
  inputProps,
  name,
  placeholder,
  fullWidth = true,
  isRequired,
  onChange,
  variant = 'outlined',
  labelProps,
  margin, ...props }, ref) => {
  return (
    <TextField
      id={id}
      label={label}
      fullWidth={fullWidth}
      type={type}
      required={isRequired}
      value={value}
      className={className || ''}
      variant={variant || 'standard'}
      placeholder={placeholder || ''}
      inputProps={inputProps}
      InputLabelProps={labelProps || {}}
      name={name}
      onChange={onChange}
      margin={margin || 'normal'}
      inputRef={ref}
      {...props}
    />
  );
}));

export const TimePicker: FC<any> = forwardRef(({...props}, ref) => {
  return(
    <DatePicker
      slotProps={{ textField: { 
        variant: 'outlined',  
        name: props.name,  
        inputProps: {...props.inputProps, ref },
        label: i18next.t('common:settings.timePicker') 
      }}}
      className={props.className}
      value={props.value.length ? new Date(props.value) : ''}
      onChange={(date: any) => props.onChange({target:{name: 'value', value: new Date(date) || ''}})}
    />
  );
});

export const SelectField: FC<any> = forwardRef(
  ({
    items, label, onSelect, value, variant, fullWidth = true, labelClassName,
    className, name, style, margin, controlStyle, formClassName = '', size,
    ...props }, ref) : any => {
    const classes = useStyles();
    return (
      <FormControl size={size} fullWidth margin={margin || 'normal'} style={controlStyle || {}} variant={variant} className={formClassName}>
        <InputLabel shrink className={labelClassName}>
          {label}
        </InputLabel>
        <Select
          value={value}
          fullWidth
          label={label}
          className={className}
          name={name}
          style={style}
          inputRef={ref}
          onChange={onSelect}
          {...props}
        >
          {items}
        </Select>
      </FormControl>
    );
  });



ToggleField.propTypes = {
  onToggle: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  checked: PropTypes.bool,
  variant: PropTypes.string,
  classes: PropTypes.object,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  size: PropTypes.string,
  style: PropTypes.object,
  labelStyle: PropTypes.object
};

SelectField.propTypes = {
  value: PropTypes.any,
  onSelect: PropTypes.any.isRequired,
  name: PropTypes.string.isRequired,
  margin: PropTypes.string,
  customWidth: PropTypes.string,
  className: PropTypes.string,
  variant: PropTypes.string,
  items: PropTypes.array.isRequired,
  label: PropTypes.string,
  formClassName: PropTypes.string,
  style: PropTypes.object,
  controlStyle: PropTypes.object
};

FormField.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  hint: PropTypes.string,
  style: PropTypes.object,
  type: PropTypes.string.isRequired,
  floatingLabelText: PropTypes.string
};
NewTextField.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string.isRequired,
  style: PropTypes.object,
  type: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  variant: PropTypes.string,
  className: PropTypes.string,
  inputProps: PropTypes.object,
  labelProps: PropTypes.object,
  margin: PropTypes.string,
  isRequired: PropTypes.bool
};
